/**
 * Ryan O'Dowd
 * 2024-09-22
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  TouchableOpacity,
  View,
} from 'react-native';
import CPassage from '@classes/Passage';
import PassageDisplay from '@components/PassageDisplay';
import ProgressIndicator from '@components/ProgressIndicator';
import React from 'react';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const BookProgress = (props: BookProgressProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const preferredTranslation = hooks.usePreferredTranslation();
  const settingsPreferredVerseSelectionMethod = hooks.useTypedSelector((state) => state.settingsPreferredVerseSelectionMethod);

  const selectedChapter = hooks.useTypedSelector((state) => state.chapters.find((chapter) => chapter.book_id === props.book.id && chapter.chapter_number === props.selectedChapterNumber) || state.chapters[0]); // @TODO: better default value
  const wrinkles = hooks.useTypedSelector((state) => state.wrinkles);

  const wrinkleUnstartedVerseNumbersByChapter = {};
  const wrinkleInProgressVerseNumbersByChapter = {};
  const wrinkledVerseNumbersByChapter = {};
  const noWrinkleVerseNumbersByChapter = {};
  Object.keys(props.book.chapters_and_verses).forEach((chapterNum: any) => { // @TODO: these calculations can probably be stored in redux (denormalized) instead of calculating in a render function
    chapterNum = +chapterNum;
    wrinkleUnstartedVerseNumbersByChapter[chapterNum] = [];
    wrinkleInProgressVerseNumbersByChapter[chapterNum] = [];
    wrinkledVerseNumbersByChapter[chapterNum] = [];
    noWrinkleVerseNumbersByChapter[chapterNum] = [];
    [...Array(props.book.chapters_and_verses[chapterNum])].forEach((__, index) => {
      const verseNum = index + 1; // @TODO: use utility function based on verseId
      if (utilities.isVerseWrinkled(wrinkles, CPassage.getVerseApiId(props.book.api_id, chapterNum, verseNum))) {
        wrinkledVerseNumbersByChapter[chapterNum].push(verseNum);
      } else if (utilities.isVerseWrinkleInProgress(wrinkles, CPassage.getVerseApiId(props.book.api_id, chapterNum, verseNum))) {
        wrinkleInProgressVerseNumbersByChapter[chapterNum].push(verseNum);
      } else if (utilities.isVerseWrinkleUnstarted(wrinkles, CPassage.getVerseApiId(props.book.api_id, chapterNum, verseNum))) {
        wrinkleUnstartedVerseNumbersByChapter[chapterNum].push(verseNum);
      } else {
        noWrinkleVerseNumbersByChapter[chapterNum].push(verseNum);
      }
    });
  });


  return (
    <View style={styles.chapterNumbersWrapper}>
      {[...Array(Object.keys(props.book.chapters_and_verses).length)].map((__, index) => {
        const chapterNumber = index + 1;
        const isSelectedChapterNumber = props.selectedChapterNumber === chapterNumber;
        const isCurrentBookChapterNumber = false; // @TODO: props.book.id === props.route.params.selectedChapter?.book_id && props.route.params.selectedChapter?.chapter_number === chapterNumber;
        if (props.selectedChapterNumber && !isSelectedChapterNumber) {
          return null;
        }
        return (
          <View
            key={chapterNumber}
            style={[
              styles.chapterAndMetadataWrapper,
              props.selectedChapterNumber && styles.selectedChapterAndMetadataWrapper,
            ]}
          >
            <View style={isCurrentBookChapterNumber && styles.currentBookChapterReadingWrapper}>
              <TouchableOpacity
                style={[
                  styles.chapterNumber,
                  utilities.isChapterWrinkled(wrinkles, props.book, chapterNumber) && styles.wrinkledChapter,
                  isSelectedChapterNumber && styles.selectedChapter,
                  wrinkledVerseNumbersByChapter[chapterNumber].length === props.book.chapters_and_verses[chapterNumber] && styles.completedChapter,
                ]}
                disabled={isSelectedChapterNumber}
                onPress={() => {
                  props.onChapterNumberSelect(chapterNumber);
                  if (props.chapterSelectionOnly) {
                    props.onSelectPassage({
                      bookId: props.book.id,
                      chapterNumber,
                    });
                  }
                }}
              >
                <ProgressIndicator
                  completedNum={wrinkledVerseNumbersByChapter[chapterNumber].length}
                  inProgressNum={wrinkleInProgressVerseNumbersByChapter[chapterNumber].length}
                  unstartedNum={wrinkleUnstartedVerseNumbersByChapter[chapterNumber].length}
                  totalNum={props.book.chapters_and_verses[chapterNumber]}
                  showInnerText={false}
                  size={52}
                  color={styles.colors.accent}// @TODO: forces wrinkled verses to be accent instead of complete...this should be designed better (graphic design and component)
                >
                  <Text style={[styles.chapterNumberText, isSelectedChapterNumber && styles.selectedChapterText]}>
                    {chapterNumber}
                  </Text>
                </ProgressIndicator>
              </TouchableOpacity>
            </View>
            {props.selectedChapterNumber &&
              <View style={styles.chapterMetadata}>
                <Text style={styles.instructionsText}>{/* @TODO: should be better */}
                  {(() => {
                    if (!props.selectedVerseNums?.length) {
                      return i18n('selectVerses');
                    }
                    return i18n('saveYourSelection');
                  })()}
                </Text>
              </View>
            }
          </View>
        );
      })}
      {(props.selectedChapterNumber && !props.chapterSelectionOnly) &&
        <View style={styles.versesWrapper}>
          <PassageDisplay
            onUpdateVerseSelection={(verseNums) => props.setSelectedVerseNums?.(verseNums)}
            passages={[new CPassage(-1, preferredTranslation.id, props.book.id, props.selectedChapterNumber, 1, selectedChapter.number_of_verses || 1)]}
            hideAddToGoal={true}
            hideTest={true}
            hideLesson={true}
            hideLicensing={true}
            showFormatting={false}
            showFootnotes={false}
            showHeadings={true}
            showWrinkleBadges={false}
            verseNumbersOnly={settingsPreferredVerseSelectionMethod === 'numbers'}
            alwaysShowSelectionMenu={true}
          />
        </View>
      }
    </View>
  );
};

class BookProgressProps {
  book: any;
  onChapterNumberSelect: (chapterNumber: number) => void; // @TODO: verify signature

  // @TODO: making these optional may cause some problems (necessary for super search though) {
  selectedChapterNumber?: number;
  selectedVerseNums?: number[];
  setSelectedVerseNums?: (verseNums: number[]) => void; // @TODO: verify signature
  chapterSelectionOnly?: boolean;
  onSelectPassage?: any; // @TODO: verify signature
  // @TODO: making these optional may cause some problems (necessary for super search though) }
};

export default BookProgress;
